<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { Loader, TableSkeletonLoader, addCursorLoader, removeCursorLoader, useToasterInSetup } from 'dmui_components'
import IroReportsList from './IroReportsList.vue'
import IroUpload from './upload/IroUpload.vue'
import ReportIRORepository, { APIErrors } from '../repository/ReportIRORepository.js'
import iroWorksheetParser, { SpreadsheetErrors } from '../iro/upload/IROWorksheetParser.js'

defineProps({
	readonly: {
		type: Boolean,
		default: false
	}
})

const iroData = ref()

const DEFAULT_MODIFIERS = {
	search: { org_name: '' }
}
defineEmits(['upload'])

const modifiers = ref()

const loading = ref(false)

async function fetchReports() {
	loading.value = true
	try {
		iroData.value = await ReportIRORepository.getCsrdIroList(modifiers.value)
	} catch (e) {
		console.error(e)
	} finally {
		loading.value = false
	}
}

watch(
	modifiers,
	async (newSearch) => {
		localStorage.setItem('report_list_iro_modifiers', JSON.stringify(newSearch))
		await fetchReports()
	},
	{ deep: true }
)

onMounted(async () => {
	const savedSearch = localStorage.getItem('report_list_iro_modifiers')
	if (savedSearch) {
		modifiers.value = JSON.parse(savedSearch)
	} else {
		modifiers.value = { ...DEFAULT_MODIFIERS }
	}
})

async function updateStatus(reportIro) {
	try {
		await ReportIRORepository.updateCsrdIroStatus(reportIro)
	} catch (e) {
		console.error(e)
	}
}

const toaster = useToasterInSetup({ page: 'IroReportsTab' }, onUnmounted)

function handleError(e) {
	if (e instanceof APIErrors || e instanceof SpreadsheetErrors) {
		toaster.show(e.detail.map((error) => `${error.path}: ${error.message}`).join('<br/>'), { type: 'error' })
	} else {
		toaster.show(e, { type: 'error' })
		console.error('File upload error', { e })
	}
}

async function onUploadData({ filename, worksheets }) {
	try {
		toaster.dismiss()
		addCursorLoader()
		const extractedIROData = iroWorksheetParser.getIROsFromSpreadsheet(worksheets)
		await ReportIRORepository.postIroList(extractedIROData)
		toaster.show(`${filename} uploaded successfully and is being processed in the background. Please allow some time and refresh the page to see results`, { type: 'success' })
	} catch (e) {
		handleError(e)
	} finally {
		removeCursorLoader()
	}
}
</script>
<template>
	<Loader v-if="!iroData" />
	<div v-else>
		<IroUpload :readonly="readonly" @upload="onUploadData" />
		<IroReportsList v-model:search="modifiers.search" :iro-data="iroData" :readonly="readonly" @update:status="updateStatus">
			<TableSkeletonLoader v-if="loading" :n-rows="20" :n-columns="4" />
		</IroReportsList>
	</div>
</template>

<style scoped lang="less">
.column-order {
	gap: @micro;
}

:deep(.column-order-icon button) {
	display: none; // TODO: can be removed once the component is updated to be able to handle search independently
}
</style>
